// Docs: https://learn.microsoft.com/en-us/aspnet/core/signalr/javascript-client?view=aspnetcore-8.0&tabs=visual-studio-code
import * as signalR from '@microsoft/signalr'
import { API_ACCESS_TOKEN } from '@services/baseService'
import appStore from '@stores/appStore'
import dashboardStore from '@stores/dashboard'
import { FROM_MODULE_TYPE } from '@utils/constants'
import { showNotification } from '@utils/errors'
import _ from 'lodash'
import { makeAutoObservable } from 'mobx'

import { ActivityType, UserActivity } from './types'

class RealtimeNotificationStore {
  constructor() {
    makeAutoObservable(this)
  }

  connection: signalR.HubConnection
  activityLogs: UserActivity[] = []
  private isStarting = false
  private retryTimeout: ReturnType<typeof setTimeout> | null = null
  private retryCount = 0
  private static readonly MAX_RETRIES = 3

  private registerEventListeners() {
    AppConsole.log('Registering event listeners...')
    this.onReceiveActivityLog()
    this.onActivityLogsResult()
  }

  private unRegisterEventListeners() {
    AppConsole.log('Unregistering event listeners...')
    this.connection.off('ReceiveActivityLog')
    this.connection.off('ActivityLogsResult')
  }

  getUserActivities(
    fromModule: FROM_MODULE_TYPE,
    objectId: string,
    activity: ActivityType
  ) {
    // remove current user and filter by fromModule, objectId, activityType
    const result = Array.from(this.activityLogs.values()).filter(
      item =>
        item.fromModule === fromModule &&
        item.objectId === objectId &&
        item.activityType === activity &&
        item.userId !== appStore.currentUser?.id
    )

    return result
  }

  getCurrentTabActivities() {
    if (
      dashboardStore.currentTab?.fromModule &&
      dashboardStore.currentTab?.objectId
    ) {
      AppConsole.log(
        'Fetching activity logs for ' +
          dashboardStore.currentTab?.fromModule +
          ' with ID: ' +
          dashboardStore.currentTab?.objectId +
          '...'
      )
      return this.getUserActivities(
        dashboardStore.currentTab?.fromModule,
        dashboardStore.currentTab?.objectId,
        'ITEM_VIEWING'
      )
    }
    return []
  }

  async setupRealtimeNotification() {
    if (this.connection) {
      return
    }

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(process.env.VITE_API_URL + '/signalr-hubs/notification', {
        accessTokenFactory: () => API_ACCESS_TOKEN,
      })
      .withAutomaticReconnect([0, 10000, 30000])
      .configureLogging(signalR.LogLevel.Information)
      .build()

    this.connection.onreconnecting(error => {
      AppConsole.log('SignalR Reconnecting...', error)
    })

    this.connection.onreconnected(() => {
      AppConsole.log('SignalR Reconnected.')
      this.getCurrentTabActivities()
    })

    // onclose only fires when automatic reconnect exhausts all retries
    this.connection.onclose(() => {
      AppConsole.log('SignalR Connection closed after all automatic retries.')
      this.scheduleReconnect()
    })

    // Register event listeners once — they persist across automatic reconnects
    this.registerEventListeners()

    await this.startConnection()
  }

  private async startConnection() {
    if (this.isStarting) return
    this.isStarting = true

    try {
      await this.connection.start()
      AppConsole.log('SignalR Connected.')
      this.retryCount = 0
      this.getCurrentTabActivities()
    } catch (err) {
      AppConsole.log('SignalR Connection failed:', err)
      this.scheduleReconnect()
    } finally {
      this.isStarting = false
    }
  }

  private scheduleReconnect() {
    if (this.retryCount >= RealtimeNotificationStore.MAX_RETRIES) {
      AppConsole.log(
        `SignalR: Stopped retrying after ${RealtimeNotificationStore.MAX_RETRIES} failed attempts.`
      )
      return
    }

    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout)
    }

    this.retryCount++
    const delay = Math.min(5000 * this.retryCount, 30000)
    AppConsole.log(
      `SignalR: Retry ${this.retryCount}/${
        RealtimeNotificationStore.MAX_RETRIES
      } in ${delay / 1000}s...`
    )

    this.retryTimeout = setTimeout(() => {
      this.retryTimeout = null
      this.startConnection()
    }, delay)
  }

  onNotification = callback => {
    if (this.connection) {
      this.connection.on('ReceiveMessage', callback)
    }
  }

  sendRequest = (functionName, data) => {
    if (this.connection) {
      return this.connection.invoke(functionName, JSON.stringify(data))
    }
  }

  logActivity = (
    fromModule: FROM_MODULE_TYPE,
    objectId: string,
    activityType: ActivityType
  ) => {
    AppConsole.log(
      'Logging activity for ' +
        fromModule +
        ' with ID: ' +
        objectId +
        ' and type: ' +
        activityType
    )

    this.sendRequest('AddActivityLog', {
      FromModule: fromModule,
      ObjectId: objectId,
      ActivityType: activityType,
    })

    this.getActivityLogs(fromModule, objectId, activityType)
  }

  onReceiveActivityLog = () => {
    this.connection.on('ReceiveActivityLog', activity => {
      const isSameObjectId =
        dashboardStore.currentTab &&
        dashboardStore.currentTab?.objectId == activity.objectId &&
        dashboardStore.currentTab?.fromModule == activity.fromModule

      if (isSameObjectId) {
        if (activity.activityType == 'ITEM_VIEWING') {
          showNotification(
            'User Editing Item',
            `${activity.user?.name} is viewing this record`,
            'info'
          )
        }

        if (activity.activityType == 'ITEM_DETAIL_CLOSED') {
          this.getCurrentTabActivities()
        }
      }

      this.getActivityLogs(
        activity.fromModule,
        activity.objectId,
        activity.activityType
      )
    })
  }

  getActivityLogs = (
    fromModule: FROM_MODULE_TYPE,
    objectId: string,
    activityType: ActivityType
  ) => {
    AppConsole.log(
      'Fetch activity logs for module:',
      fromModule,
      'objectId:',
      objectId
    )
    return this.sendRequest('GetActivityLogs', {
      FromModule: fromModule,
      ObjectId: objectId,
      ActivityType: activityType,
    })
  }

  clearActivityLogs = () => {
    this.activityLogs = []
  }

  onActivityLogsResult = () => {
    if (this.connection) {
      this.connection.on('ActivityLogsResult', activityLogs => {
        AppConsole.log('Received activity logs:', activityLogs)
        this.activityLogs = this.removeDuplicateLogs(activityLogs)
      })
    }
  }

  private removeDuplicateLogs(logs: UserActivity[]) {
    // remove duplicates based on fromModule, objectId, activityType, userId
    const uniqueLogs = new Map<string, UserActivity>()

    logs.forEach(item => {
      const key = `${item.fromModule}-${item.objectId}-${item.activityType}-${item.userId}`
      if (!uniqueLogs.has(key)) {
        uniqueLogs.set(key, item)
      }
    })

    return Array.from(uniqueLogs.values())
  }

  private getActivityKey(item: UserActivity): string {
    return `${item.fromModule}-${item.objectId}-${item.activityType}-${item.userId}`
  }
}

export default new RealtimeNotificationStore()
